import { useEffect, useRef, useState } from 'react';
import {
  Button, FileInput, Icon, Select, TextField, Tooltip, Typography,
} from '@gupshup-frontend/gupshup-ts-components';
import {
  HEADER_TYPE, MEDIA_TYPE, MEDIA_RULES, isAuthentication, isCarousel, isLto, isCallPermissionRequest,
  hasRequestContactInfo,
} from '../state';
import { checkHeaderTextFormat } from '../validate';
import { friendlyError } from './errorMessages';
import { HeaderSampleFields } from './Variables';

const HEADER_TEXT_MAX_CHARS = 60;
const HEADER_VARIABLE_TOKEN = '{{1}}';

// No console copy exists for a Location-header helper line (gs-ui-templates' AddSampleMedia.tsx
// simply renders nothing for mediaType.location — verified, not just unfound) — written here.
const LOCATION_HEADER_HELPER_TEXT = 'The location is supplied by your integration when the message is sent — no sample media is needed here.';

// Console-verbatim (gs-ui-templates en.json's choose_header_media_image_video, read by
// Header/index.tsx:131 only while isLto()).
const LTO_HEADER_HELPER_TEXT = 'Choose the header media Image / Video';

const HEADER_TYPE_LABELS = {
  [HEADER_TYPE.NONE]: 'None',
  [HEADER_TYPE.TEXT]: 'Text',
  [MEDIA_TYPE.IMAGE]: 'Image',
  [MEDIA_TYPE.VIDEO]: 'Video',
  [MEDIA_TYPE.DOCUMENT]: 'Document',
  [MEDIA_TYPE.LOCATION]: 'Location',
  [MEDIA_TYPE.GIF]: 'GIF',
};

// GIF used to be withheld for a NEW template — not an Enterprise-side gate (Enterprise only
// rejects a non-MM-Lite account, "GIF template type is only allowed for MMLite enabled
// accounts.", ce-api MessageTemplate.java:473-479) but because the shared send component this app
// also uses for the Message tab, @gs/gs-integration-component, can't send a GIF at all — its own
// Save-time check, isDocumentUrlValid, has no GIF case and always returns false for one
// (node_modules/@gs/gs-integration-component/src/Helper/Utils.tsx:252-260), which its Footer's
// submit guard turned into a silently blocked Save with no usable error
// (node_modules/@gs/gs-integration-component/src/Components/Footer/index.tsx:56). GIF sends now
// go through src/templateSend's Standard panel instead of that component, so the dead end no
// longer applies — re-offered here like every other media type. LOCATION carries no such gate
// either (console's HEADER_OPTIONS offers it unconditionally, config.ts:240-245).
const OFFERED_HEADER_TYPES = [
  HEADER_TYPE.NONE,
  HEADER_TYPE.TEXT,
  MEDIA_TYPE.IMAGE,
  MEDIA_TYPE.VIDEO,
  MEDIA_TYPE.DOCUMENT,
  MEDIA_TYPE.LOCATION,
  MEDIA_TYPE.GIF,
];

// Two subcategories still withhold part of the list above, per-state rather than statically:
//   - LTO drops everything except None/Image/Video. Console's own switch (Header/index.tsx:70-89)
//     hides TEXT, DOCUMENT, LOCATION and GIF for isLto() — NONE and IMAGE/VIDEO are the only cases
//     that switch doesn't hide for it, so those three are what's actually offered, not "everything
//     except GIF" as this comment used to (mis)read.
//   - Call Permission Request drops every media type, down to None/Text only — Enterprise
//     requires type TEXT for it (ce-api ValidationUtils.java:734-749); the console hides every
//     media header type for it too (Header/index.tsx:76-82), including GIF, even though GIF's own
//     case in that same switch doesn't explicitly list it — this form applies the stricter,
//     backend-verified rule rather than copying that gap forward.
function buildOfferedHeaderTypes(state) {
  if (isCallPermissionRequest(state)) {
    return [HEADER_TYPE.NONE, HEADER_TYPE.TEXT];
  }
  if (isLto(state)) {
    return [HEADER_TYPE.NONE, MEDIA_TYPE.IMAGE, MEDIA_TYPE.VIDEO];
  }
  return OFFERED_HEADER_TYPES;
}

function buildHeaderTypeOptions(selectedValue, offeredTypes) {
  const options = offeredTypes.map((value) => ({
    value,
    label: HEADER_TYPE_LABELS[value],
    'data-testid': `bloomreach-templates-form-header-option-${value.toLowerCase()}`,
  }));
  if (!offeredTypes.includes(selectedValue)) {
    options.push({
      value: selectedValue,
      label: HEADER_TYPE_LABELS[selectedValue],
      disabled: true,
      'data-testid': `bloomreach-templates-form-header-option-${selectedValue.toLowerCase()}`,
    });
  }
  return options;
}

// Console's per-media-type copy (format description only, task-verified against the console —
// not derivable from MEDIA_RULES.accept). The size half of both the upload hint and the
// too-large error is appended from MEDIA_RULES at render/error time instead of being hardcoded
// here, so the size shown can never drift from mediaRule.maxSizeBytes — the size actually
// enforced in handleFileChange below.
const MEDIA_TYPE_COPY = {
  [MEDIA_TYPE.IMAGE]: {
    uploadHint: 'Supported image formats are PNG and JPEG',
    wrongTypeError: 'Uploaded file should be in JPG, JPEG or PNG format',
  },
  [MEDIA_TYPE.VIDEO]: {
    uploadHint: 'MP4 video format is supported',
    wrongTypeError: 'Uploaded file should be in MP4 format',
  },
  [MEDIA_TYPE.DOCUMENT]: {
    uploadHint: 'PDF documents are supported',
    wrongTypeError: 'Uploaded file should be in PDF format',
  },
  [MEDIA_TYPE.GIF]: {
    uploadHint: 'MP4 video format is supported',
    wrongTypeError: 'Uploaded file should be in MP4 format',
  },
};

// state.js's maxSizeLabel is unspaced ("5MB") since MEDIA_RULES is the shared source both this
// file and Carousel.js read the enforced byte limit from; the console writes the unit with a
// leading space ("5 MB"). Reformat only here, at display time, rather than editing state.js.
function spacedMaxSize(rule) {
  return rule.maxSizeLabel.replace('MB', ' MB');
}

// 4. Header — a Select of None/Text/Image/Video/Document/Location/GIF, revealing a text field or
// a FileInput depending on the choice (Location gets neither — see the isMediaHeader branch below).
const HeaderSection = ({ state, dispatch, errors, disabled }) => {
  // Hooks must run on every render regardless of the isAuthentication/isCarousel early-return
  // below, so they're declared ahead of that check — state.header always exists (initialFormState's
  // own default shape) even for the categories that don't render this section.
  const headerTextRef = useRef(null);

  // Local preview only — createObjectURL never touches the network or uploads anything, same
  // pattern Form/index.js already runs for the live WhatsApp-mockup pane (its own
  // mediaPreviewUrl effect). Keyed on the File itself so picking a replacement swaps the URL
  // instead of stacking blob references, and the cleanup below revokes the previous URL on every
  // change and again on unmount — otherwise each pick leaks a blob URL for the tab's lifetime.
  const pickedFile = state.header.headerMedia.file;
  const [previewUrl, setPreviewUrl] = useState('');
  useEffect(() => {
    if (!pickedFile) {
      setPreviewUrl('');
      return undefined;
    }
    const url = URL.createObjectURL(pickedFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pickedFile]);

  // Edit-mode fallback: prefill.js populates headerMedia.mediaSrc with the existing media's CDN
  // URL when a template is opened for edit with no freshly picked file yet. previewUrl (a local
  // blob URL, above) always wins once a file IS picked — handleHeaderSelectChange and
  // handleFileChange below both blank mediaSrc the moment the media type changes or a new file is
  // picked, so the two are already mutually exclusive in practice; the `||` just makes that
  // precedence explicit. mediaSrc is used directly as a URL below — never handed to
  // createObjectURL/revokeObjectURL, since it's already a real (possibly cross-origin) URL, not a
  // blob this component owns the lifecycle of.
  const currentMediaSrc = previewUrl || state.header.headerMedia.mediaSrc || '';
  // A remote mediaSrc can fail to load (expired link, CDN hiccup, cross-origin block) in a way a
  // freshly created blob URL practically never does — resets whenever the src being shown changes
  // so a stale failure doesn't keep hiding a perfectly good new preview.
  const [mediaLoadError, setMediaLoadError] = useState(false);
  useEffect(() => {
    setMediaLoadError(false);
  }, [currentMediaSrc]);

  if (isAuthentication(state) || isCarousel(state) || hasRequestContactInfo(state)) {
    // Authentication templates have no header in WhatsApp's spec; Carousel templates use
    // carousels.mediaType instead of header (out of scope here); Request Contact Info rejects any
    // header outright (ValidationUtils.validateRequestContactInfoTemplate) — matches gs-ui-templates
    // Header/index.tsx's own hide rule for all three cases (isShareContactInfo included).
    return null;
  }

  const { header } = state;
  const isMediaHeader = header.value === HEADER_TYPE.MEDIA;
  const selectedValue = isMediaHeader ? header.headerMedia.value : header.value;
  const mediaRule = MEDIA_RULES[header.headerMedia.value];
  const mediaCopy = MEDIA_TYPE_COPY[header.headerMedia.value];
  const mediaUploadHint = mediaRule && mediaCopy
    ? `${mediaCopy.uploadHint} (Max size ${spacedMaxSize(mediaRule)})`
    : '';
  const headerTextSubmitError = errors['header.headerText.value'];
  const headerTextDisplayError = header.headerText.error || headerTextSubmitError;
  // Mirrors the console's own isAddVarButtonDisabled (HeaderText.tsx:164-172): blocked once a
  // variable already exists (a header allows exactly one), once the field is disabled, or once
  // inserting the token would push the field past its own 60-char cap — the console hardcodes that
  // last check as `value.length > 55` (60 minus the 5 characters of "{{1}}"); computed here instead
  // of copying the magic number, so it stays correct if HEADER_TEXT_MAX_CHARS ever changes.
  const headerHasVariable = header.headerText.variable.length > 0;
  const headerVariableWouldOverflow = header.headerText.value.length + HEADER_VARIABLE_TOKEN.length > HEADER_TEXT_MAX_CHARS;
  const isAddHeaderVariableDisabled = disabled || headerHasVariable || headerVariableWouldOverflow;

  const handleHeaderSelectChange = (selected) => {
    const value = Array.isArray(selected) ? selected[0] : selected;
    const isNotMedia = value === HEADER_TYPE.NONE || value === HEADER_TYPE.TEXT;
    dispatch({ type: 'set', path: 'header.value', value: isNotMedia ? value : HEADER_TYPE.MEDIA });
    // Full replace (not a merge) — switching media type must drop any previously picked file,
    // same as gs-ui-templates Header/index.tsx's handleHeaderSelectChange.
    dispatch({
      type: 'set',
      path: 'header.headerMedia',
      value: { value: isNotMedia ? '' : value, error: '', mediaSrc: '', mediaId: '', file: null },
    });
    // `type` is the wire-level message type (Form/payload.js's top-level `type`). It always
    // tracks the header choice one-to-one: TEXT for None/Text, the media sub-type for a media
    // header — see gs-ui-templates' own generateTemplateType.
    dispatch({ type: 'set', path: 'type.value', value: isNotMedia ? 'TEXT' : value });
  };

  // Shared by typing and the Add-variable button below, so both go through the exact same dispatch
  // shape — live validation (checkHeaderTextFormat) and the Variables section's own token-watching
  // effect (sections/Variables.js, keyed on header.headerText.value) react identically either way.
  // caretPos is only ever passed by the button; a plain keystroke leaves the browser to manage the
  // textfield's own caret, same as before this change.
  const applyHeaderTextValue = (nextValue, caretPos) => {
    dispatch({
      type: 'merge',
      path: 'header.headerText',
      value: { value: nextValue, error: checkHeaderTextFormat(nextValue) },
    });
    const el = headerTextRef.current;
    if (el && caretPos !== undefined) {
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(caretPos, caretPos);
      });
    }
  };

  const handleHeaderTextChange = (event) => {
    applyHeaderTextValue(event.target.value);
  };

  // Inserts {{1}} at the caret (or at the end with no caret) — disabled state above already rules
  // out a second variable or an overflow, so this never has to re-check either.
  const handleAddHeaderVariable = () => {
    const el = headerTextRef.current;
    const text = header.headerText.value;
    const pos = el ? (el.selectionStart ?? text.length) : text.length;
    const next = `${text.slice(0, pos)}${HEADER_VARIABLE_TOKEN}${text.slice(pos)}`;
    applyHeaderTextValue(next, pos + HEADER_VARIABLE_TOKEN.length);
  };

  const handleFileChange = (files) => {
    const file = files && files[0];
    if (!file || !mediaRule) return;
    if (!mediaRule.accept.includes(file.type)) {
      dispatch({
        type: 'merge',
        path: 'header.headerMedia',
        value: { error: mediaCopy.wrongTypeError, file: null, mediaId: '' },
      });
      return;
    }
    if (file.size > mediaRule.maxSizeBytes) {
      dispatch({
        type: 'merge',
        path: 'header.headerMedia',
        value: { error: `File size exceeding maximum limit of ${spacedMaxSize(mediaRule)}`, file: null, mediaId: '' },
      });
      return;
    }
    // No upload call here — this task only stores the picked File; it rides as the multipart
    // `header_examples` part at submit time (api/templates.js's buildTemplateFormData).
    // Form/validate.js still gates on `.mediaId` being non-blank (its own pre-upload-flow
    // presence check) but Form/payload.js never reads `.mediaId` at all, so any non-blank marker
    // satisfies that check without misrepresenting a real server-issued media id on the wire.
    dispatch({
      type: 'merge',
      path: 'header.headerMedia',
      value: { error: '', file, mediaSrc: '', mediaId: file.name },
    });
  };

  const handleRemoveFile = () => {
    dispatch({
      type: 'merge',
      path: 'header.headerMedia',
      value: { file: null, mediaId: '', mediaSrc: '', error: '' },
    });
  };

  // Shows the freshly picked file's local preview, or — once prefill.js has populated it — the
  // existing media's own CDN URL via currentMediaSrc above. Mirrors TemplatePreview/Header.js's
  // own renderMedia switch (video via a <source> child, document via an iframe) so this inline
  // preview looks and behaves the same as the live mockup pane's. mediaLoadError hides the element
  // instead of leaving a broken image/video/iframe on screen when a remote mediaSrc doesn't
  // actually load.
  const renderMediaPreview = () => {
    if (!currentMediaSrc || mediaLoadError) return null;
    if (header.headerMedia.value === MEDIA_TYPE.IMAGE) {
      return (
        <img
          src={currentMediaSrc}
          alt="Sample media preview"
          className="br-tpl-form-media-preview"
          onError={() => setMediaLoadError(true)}
          data-testid="bloomreach-templates-form-header-media-preview"
        />
      );
    }
    if (header.headerMedia.value === MEDIA_TYPE.VIDEO || header.headerMedia.value === MEDIA_TYPE.GIF) {
      return (
        <video
          controls
          className="br-tpl-form-media-preview"
          onError={() => setMediaLoadError(true)}
          data-testid="bloomreach-templates-form-header-media-preview"
        >
          <source src={currentMediaSrc} type="video/mp4" />
        </video>
      );
    }
    if (header.headerMedia.value === MEDIA_TYPE.DOCUMENT) {
      return (
        <iframe
          src={currentMediaSrc}
          title="Sample document preview"
          className="br-tpl-form-media-preview"
          onError={() => setMediaLoadError(true)}
          data-testid="bloomreach-templates-form-header-media-preview"
        />
      );
    }
    return null;
  };

  return (
    <div className="br-tpl-form-card" data-testid="bloomreach-templates-form-header">
      <div className="br-tpl-form-card-heading-row">
        <div className="br-tpl-form-card-heading">
          <Typography variant="subtitle1" data-testid="bloomreach-templates-form-header-heading">
            Header
          </Typography>
          <span className="br-tpl-form-optional-pill" data-testid="bloomreach-templates-form-header-optional">
            Optional
          </span>
        </div>
        <Tooltip
          placement="left"
          title="Image, document and video will be considered as Media type"
          data-testid="bloomreach-templates-form-header-tooltip"
        >
          <span>
            <Icon name="question" size={18} showCursor />
          </span>
        </Tooltip>
      </div>
      <Typography variant="caption" className="br-tpl-form-helper-text">
        {isLto(state)
          ? LTO_HEADER_HELPER_TEXT
          : 'Add a 60 character title to your message. Only one variable is supported in the header.'}
      </Typography>

      <Select
        label="Select"
        items={buildHeaderTypeOptions(selectedValue, buildOfferedHeaderTypes(state))}
        value={selectedValue}
        handleChange={handleHeaderSelectChange}
        disabled={disabled}
        width="100%"
        data-testid="bloomreach-templates-form-header-select"
      />
      {errors['header.headerMedia.value'] && (
        <Typography variant="caption" color="error" data-testid="bloomreach-templates-form-header-type-error">
          {friendlyError(errors['header.headerMedia.value'])}
        </Typography>
      )}

      {header.value === HEADER_TYPE.TEXT && (
        <div className="br-tpl-form-header-text">
          <TextField
            fullWidth
            label="Header Text"
            value={header.headerText.value}
            onChange={handleHeaderTextChange}
            disabled={disabled}
            placeholder="Enter header text"
            error={Boolean(headerTextDisplayError)}
            helperText={friendlyError(headerTextDisplayError)}
            autoComplete="off"
            inputRef={headerTextRef}
            inputProps={{
              maxLength: HEADER_TEXT_MAX_CHARS,
              'data-testid': 'bloomreach-templates-form-header-text-field',
            }}
          />
          <div className="br-tpl-form-header-text-actions">
            <Tooltip
              placement="bottom"
              title={isAddHeaderVariableDisabled ? 'Only one variable is allowed' : ''}
              data-testid="bloomreach-templates-form-header-add-variable-tooltip"
            >
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon="plus"
                  disabled={isAddHeaderVariableDisabled}
                  onClick={handleAddHeaderVariable}
                  data-testid="bloomreach-templates-form-header-add-variable"
                >
                  Add variable
                </Button>
              </span>
            </Tooltip>
            <Typography
              variant="caption"
              className="br-tpl-form-char-count"
              data-testid="bloomreach-templates-form-header-text-count"
            >
              {`${header.headerText.value.length} / ${HEADER_TEXT_MAX_CHARS}`}
            </Typography>
          </div>
          {errors['header.headerText.variable'] && (
            <Typography variant="caption" color="error" data-testid="bloomreach-templates-form-header-variable-error">
              {friendlyError(errors['header.headerText.variable'])}
            </Typography>
          )}
          {/* B4: sample-value input for the header's {{1}} token, moved in from the old
              standalone Variables card — console parity (HeaderText.tsx:229-234 mounts its own
              Variables the same way, in the same spot). headerHasVariable already covers "header
              is TEXT and has a variable" together with the header.value === HEADER_TYPE.TEXT
              check this whole block is already inside. */}
          {headerHasVariable && (
            <HeaderSampleFields state={state} dispatch={dispatch} errors={errors} disabled={disabled} />
          )}
        </div>
      )}

      {/* Location carries no sample media at all — no header text, no upload, no preview, see
          gs-ui-templates' own AddSampleMedia.tsx:175 (`headerMediaType === mediaType.location`
          returns null there too). Just a short explainer instead of the media block below. */}
      {isMediaHeader && header.headerMedia.value === MEDIA_TYPE.LOCATION && (
        <Typography
          variant="caption"
          className="br-tpl-form-helper-text"
          data-testid="bloomreach-templates-form-header-location-helper"
        >
          {LOCATION_HEADER_HELPER_TEXT}
        </Typography>
      )}

      {isMediaHeader && header.headerMedia.value !== MEDIA_TYPE.LOCATION && (
        <div className="br-tpl-form-header-media">
          <Typography variant="subtitle1" data-testid="bloomreach-templates-form-header-media-heading">
            Add Sample Media*
          </Typography>
          <Typography
            variant="caption"
            className="br-tpl-form-helper-text"
            data-testid="bloomreach-templates-form-header-media-helper"
          >
            Only one sample media is allowed
          </Typography>
          {header.headerMedia.file ? (
            <>
              {renderMediaPreview()}
              <div className="br-tpl-form-media-picked">
                <Typography variant="body2" noWrap data-testid="bloomreach-templates-form-header-media-filename">
                  {header.headerMedia.file.name}
                </Typography>
                <Button
                  size="small"
                  variant="text"
                  color="error"
                  startIcon="x"
                  disabled={disabled}
                  onClick={handleRemoveFile}
                  data-testid="bloomreach-templates-form-header-media-remove"
                >
                  Remove
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Edit-mode only: no file picked yet, but the template already has header media —
                  show what's live today so the user isn't editing blind. Enterprise still requires
                  a fresh upload on every media edit (validate.js), so the FileInput below stays
                  mandatory regardless — this is informational, not a substitute for it. */}
              {header.headerMedia.mediaSrc && !mediaLoadError && (
                <>
                  {renderMediaPreview()}
                  <Typography
                    variant="caption"
                    className="br-tpl-form-helper-text"
                    data-testid="bloomreach-templates-form-header-media-current-caption"
                  >
                    Current media — upload a file to keep or replace it
                  </Typography>
                </>
              )}
              <FileInput
                fullWidth
                inputName="header_media"
                fileType={(mediaRule?.accept || []).join(',')}
                showLoader={false}
                handleFiles={handleFileChange}
                disabled={disabled}
                error={Boolean(header.headerMedia.error)}
                defaultText={mediaUploadHint}
                existingSelectedFiles={[]}
                data-testid="bloomreach-templates-form-header-media-fileinput"
              />
            </>
          )}
          {header.headerMedia.error && (
            <Typography variant="caption" color="error" data-testid="bloomreach-templates-form-header-media-error">
              {header.headerMedia.error}
            </Typography>
          )}
          {errors['header.headerMedia.mediaId'] && (
            <Typography
              variant="caption"
              color="error"
              data-testid="bloomreach-templates-form-header-media-id-error"
            >
              {friendlyError(errors['header.headerMedia.mediaId'])}
            </Typography>
          )}
        </div>
      )}
    </div>
  );
};

export default HeaderSection;
